const API = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";
export const proofUrl = (key: string) => `${API}/v1/media/private/${key}`;
export const WEB = (import.meta.env.VITE_WEB_URL as string | undefined) ?? "http://localhost:5173";

export class ApiError extends Error {
  constructor(public code: string, message: string, public status: number) { super(message); }
}

export async function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, {
    method: init.method ?? (init.body !== undefined ? "POST" : "GET"),
    credentials: "include",
    headers: init.body !== undefined ? { "content-type": "application/json" } : {},
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string }; message?: string } | null;
    throw new ApiError(j?.error?.code ?? "http_error", j?.error?.message ?? j?.message ?? res.statusText, res.status);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export interface Person { id: string; name: string; phone: string | null; email: string }
export interface Payment { id: string; bookingId: string; kind: "deposit" | "balance" | "refund"; status: "due" | "submitted" | "verified" | "rejected" | "void"; method: string | null; reference: string | null; amountMinor: number; currency: string; proofKey: string | null; submittedAt: string | null }
export interface BoardRow {
  id: string; code: string; status: string; ownerType: "platform" | "host"; checkIn: string; checkOut: string; nights: number; guests: number;
  totalMinor: number; depositMinor: number; currency: string; respondBy: string | null; payBy: string | null; createdAt: string; guestMessage: string | null;
  listingTitle: { fr: string } | null; guest: Person | null; host: Person | null; payments: Payment[];
}
export interface BookingFile {
  booking: BoardRow & { hostCommissionMinor: number; guestFeeMinor: number; cancellationPolicy: string; cancelReason: string | null };
  guest: Person | null; host: Person | null;
  listing: { id: string; title: { fr: string }; address: string; cityKey: string; checkInTime: string; checkOutTime: string };
  payments: Payment[]; history: { id: string; action: string; actorId: string | null; createdAt: string }[];
}
export interface PayoutRow { id: string; bookingId: string; bookingCode: string; amountMinor: number; currency: string; dueAt: string; host: Person | null }
export interface RefundRow extends Payment { bookingCode: string; guest: Person | null }
export interface OutboxRow { id: string; template: string; manualLink: string | null; createdAt: string; payload: Record<string, unknown> }

export const mediaUrl = (key: string) => `${(import.meta.env.VITE_MEDIA_URL as string | undefined) ?? "http://localhost:8787/media"}/${key}`;

/** Upload an image through the API (type and size are checked server-side); returns its storage key. */
export async function uploadImage(area: "listing" | "id" | "video", file: Blob): Promise<string> {
  const res = await fetch(`${API}/v1/media/${area}`, { method: "POST", credentials: "include", headers: { "content-type": file.type || "image/jpeg" }, body: file });
  if (!res.ok) {
    const j = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new ApiError("upload_failed", j?.error?.message ?? "Échec de l'envoi", res.status);
  }
  return ((await res.json()) as { key: string }).key;
}

/** Re-encode to a 1600px-wide JPEG so phone photos upload in seconds on weak connections. */
export async function shrinkImage(file: File): Promise<Blob> {
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 1600 / bmp.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const out = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.85));
    return out && out.size < file.size ? out : file;
  } catch { return file; }
}

export type L10n = { fr: string; ar?: string; en?: string };
export interface Vocab {
  cities: { id: number; key: string; country: string; name: L10n }[];
  amenities: { id: number; key: string; category: string }[];
  categories: { id: number; key: string }[];
}
export interface CatalogRow {
  id: string; title: L10n; status: string; ownerType: "platform" | "host"; cityKey: string; hostName: string | null; basePriceMinor: number; currency: string; coverKey: string | null; maxGuests: number;
}
export interface ListingFull {
  id: string; cityId: number; status: string; ownerType: "platform" | "host"; title: L10n; description: L10n; propertyType: string; roomType: string; address: string;
  lat: number | null; lng: number | null; maxGuests: number; bedrooms: number; beds: number; bathrooms: string; basePriceMinor: number; cleaningFeeMinor: number; currency: string;
  checkInTime: string; checkOutTime: string; minNights: number; maxNights: number; cancellationPolicy: string; houseRules: string[];
  photos: { id: string; key: string; position: number }[]; videos?: { id: string; key: string; position: number }[]; amenities: { id: number }[]; categories: { id: number }[];
}
export interface HostQueueRow {
  userId: string; user: Person | undefined;
  application: { displayName: string; bio: string | null; languages: string[]; createdAt: string } | null;
  documents: { id: string; docType: string; frontKey: string; backKey: string | null; createdAt: string }[];
}
